/**
 * TiptapTemplateEditor - Rich text editor for template text with @links and <tag>labels</tag>
 *
 * Features:
 * - @ trigger opens AddLinkDialog for link input
 * - BubbleMenu on text selection for tag creation
 * - Real-time serialization to template markup format
 * - Integration with existing AddLinkDialog and AddLabelDialog
 *
 * Based on RichMentionEditor.js pattern
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapMention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { Box, Paper, Button, Divider } from '@mui/material';
import HighlightIcon from '@mui/icons-material/Highlight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { LinkMentionNode } from './extensions/LinkMentionNode';
import { CountdownMentionNode } from './extensions/CountdownMentionNode';
import { FlowVariableMentionNode } from './extensions/FlowVariableMentionNode';
import { TagMark } from './extensions/TagMark';
import {
  parseTemplateToTiptap,
  serializeTiptapToTemplate,
} from './utils/serialization';
import { MentionSuggestionMenu, type MentionSuggestionMenuRef } from './components';
import { AddLinkDialog, type LinkData } from '../AddLinkDialog';
import { AddLabelDialog } from '../AddLabelDialog';
import { AddCountdownDialog } from '../AddCountdownDialog';
import type { InlineCountdownConfig } from '@segmently/schema-models/v2';
import type { TemplateLabelBlock } from '@modules/reactFlow/types/templateLabelBlock.types';
import type { PredecessorScreen } from '@modules/reactFlow/types/predecessorScreen.types';
import { MAIN_FIELD_HEIGHT } from '../../../../config/editorFieldStyles';
import { supportsPaywallVariables, type PaywallVariable } from '../../../../config/paywallVariablesConfig';
import type { FlowVariableItem } from '../../../../config/flowVariablesConfig';

/**
 * LabelBlockType type - matches structure from @segmently/schema-models
 * Using local definition to avoid module resolution issues
 */
export type LabelBlockType = TemplateLabelBlock;

/**
 * Selection info for determining what is currently selected in the editor
 */
type SelectionInfo =
  | { type: 'link'; id: string; url: string; labelTag: string | null; from: number; to: number }
  | { type: 'countdown'; id: string; labelTag: string | null; from: number; to: number }
  | { type: 'flowVariable'; id: string; labelTag: string | null; from: number; to: number }
  | { type: 'label'; tagName: string; from: number; to: number }
  | { type: 'text'; from: number; to: number }
  | null;

/**
 * Props for TiptapTemplateEditor
 */
export interface TiptapTemplateEditorProps {
  /** Current value (template markup string) */
  value: string;

  /** Callback when value changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;

  /** Placeholder text */
  placeholder?: string;

  /** Existing links dictionary */
  existingLinks: Record<string, string>;

  /** Callback when new link is added. Optional updatedText syncs text with @linkName in single atomic update. */
  onAddLink: (name: string, url: string, updatedText?: string) => void;

  /** Existing labels dictionary */
  existingLabels: Record<string, LabelBlockType>;

  /** Callback when new label is added. Optional updatedText syncs text with <tag>text</tag> in single atomic update. */
  onAddLabel: (name: string, labelBlock: Partial<LabelBlockType>, updatedText?: string) => void;

  /** Callback when link is deleted from text */
  onDeleteLink?: (name: string, updatedText: string) => void;

  /** Existing countdowns dictionary (optional — only for CopyBlock V2 fields) */
  existingCountdowns?: Record<string, InlineCountdownConfig>;

  /** Callback when new countdown is added. Optional updatedText syncs text with @timerName in single atomic update. */
  onAddCountdown?: (name: string, config: InlineCountdownConfig, updatedText?: string) => void;

  /** Callback when countdown is deleted from text */
  onDeleteCountdown?: (name: string, updatedText: string) => void;

  /** Callback when label is deleted from text */
  onDeleteLabel?: (name: string, updatedText: string) => void;

  /** Parent Text element styles to inherit for new tags */
  parentTextStyles?: Partial<LabelBlockType>;

  /** Predecessor screens for link selection dropdown */
  predecessorScreens?: PredecessorScreen[];

  /** Screen type for conditional features (e.g., paywall variables) */
  screenType?: string;

  /** Flow variable items for @ menu (optional — V2 only). When undefined, section is hidden. */
  flowVariables?: FlowVariableItem[];

  /** Test ID for the editor */
  'data-testid'?: string;
}

/**
 * Editor container styles matching existing editorFieldStyles
 */
const editorContainerSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  backgroundColor: 'background.paper',
  minHeight: MAIN_FIELD_HEIGHT,
  '&:hover': {
    borderColor: 'text.secondary',
  },
  '&:focus-within': {
    borderColor: 'primary.main',
    borderWidth: 2,
  },
  '& .ProseMirror': {
    minHeight: MAIN_FIELD_HEIGHT - 2, // Account for border
    padding: '6px 12px',
    outline: 'none',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    '& p': {
      margin: 0,
    },
    '& p.is-editor-empty:first-of-type::before': {
      content: 'attr(data-placeholder)',
      float: 'left',
      color: 'text.disabled',
      pointerEvents: 'none',
      height: 0,
    },
  },
  // Styles for link mentions
  '& .link-mention-wrapper': {
    display: 'inline-block',
    margin: '0 2px',
    verticalAlign: 'baseline',
  },
  // Styles for countdown mentions
  '& .countdown-mention-wrapper': {
    display: 'inline-block',
    margin: '0 2px',
    verticalAlign: 'baseline',
  },
  // Styles for template tags - display as <tagName>text</tagName>
  '& .template-tag': {
    borderRadius: '4px',
    padding: '0 4px',
    // Show opening tag marker <tagName>
    '&::before': {
      content: '"<" attr(data-tag-name) ">"',
      color: '#666',
      fontSize: '0.75em',
      fontFamily: 'monospace',
    },
    // Show closing tag marker </tagName>
    '&::after': {
      content: '"</" attr(data-tag-name) ">"',
      color: '#666',
      fontSize: '0.75em',
      fontFamily: 'monospace',
    },
  },
};

const bubbleMenuPaperSx = {
  p: 0.5,
  display: 'flex',
  alignItems: 'center',
  boxShadow: 3,
};

/**
 * TiptapTemplateEditor component
 */
export function TiptapTemplateEditor({
  value,
  onChange,
  disabled = false,
  placeholder = 'Enter text...',
  existingLinks,
  onAddLink,
  existingLabels,
  onAddLabel,
  onDeleteLink,
  existingCountdowns,
  onAddCountdown,
  onDeleteCountdown,
  onDeleteLabel,
  parentTextStyles,
  predecessorScreens,
  screenType,
  flowVariables,
  'data-testid': testId,
}: TiptapTemplateEditorProps): React.ReactElement {
  // Dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [countdownDialogOpen, setCountdownDialogOpen] = useState(false);

  // Suggestion menu state (for paywall screens: Link/Variable choice)
  const [suggestionMenuOpen, setSuggestionMenuOpen] = useState(false);
  const [suggestionMenuAnchor, setSuggestionMenuAnchor] = useState<{ getBoundingClientRect: () => DOMRect } | null>(null);
  const suggestionMenuRef = useRef<MentionSuggestionMenuRef>(null);
  const suggestionRangeRef = useRef<{ from: number; to: number } | null>(null);

  // Check if paywall variables are enabled for this screen type
  const showPaywallVariables = supportsPaywallVariables(screenType);

  // Show mention menu when either paywall or flow variables are provided (even if empty array — means V2 mode)
  // V1 safety: flowVariables is undefined for V1 → hasFlowVariables is false → falls back to showPaywallVariables only
  const hasFlowVariables = flowVariables != null;
  const hasCountdowns = existingCountdowns != null;
  const showMentionMenu = showPaywallVariables || hasFlowVariables || hasCountdowns;

  // Build Set of flow variable refs for serialization (detect @variable patterns)
  const flowVariableRefs = useMemo(
    () => new Set(flowVariables?.map(fv => fv.ref) ?? []),
    [flowVariables]
  );

  // Edit mode state for dialogs
  const [linkDialogMode, setLinkDialogMode] = useState<'add' | 'edit'>('add');
  const [linkEditingKey, setLinkEditingKey] = useState<string>('');
  const [linkEditingUrl, setLinkEditingUrl] = useState<string>('');

  const [labelDialogMode, setLabelDialogMode] = useState<'add' | 'edit'>('add');
  const [labelEditingKey, setLabelEditingKey] = useState<string>('');
  const [labelEditingBlock, setLabelEditingBlock] = useState<Partial<LabelBlockType>>({});

  // Ref to track if dialog was just closed (prevents re-triggering)
  const dialogJustClosedRef = useRef(false);

  // Store selection for tag creation
  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);

  // Store selected text for label dialog pre-fill
  const selectedTextRef = useRef<string>('');

  // Flag to suppress onUpdate during programmatic changes (prevents race condition)
  const suppressUpdateRef = useRef(false);

  // Handle opening link dialog
  const handleOpenLinkDialog = useCallback(() => {
    if (linkDialogOpen) return;
    dialogJustClosedRef.current = false;
    setLinkDialogOpen(true);
  }, [linkDialogOpen]);

  // Handle closing link dialog
  const handleCloseLinkDialog = useCallback(() => {
    setLinkDialogOpen(false);
    setLinkDialogMode('add');
    setLinkEditingKey('');
    setLinkEditingUrl('');
    dialogJustClosedRef.current = true;
  }, []);

  // Handle opening label dialog
  const handleOpenLabelDialog = useCallback(() => {
    if (labelDialogOpen) return;
    setLabelDialogOpen(true);
  }, [labelDialogOpen]);

  // Handle closing label dialog
  const handleCloseLabelDialog = useCallback(() => {
    setLabelDialogOpen(false);
    setLabelDialogMode('add');
    setLabelEditingKey('');
    setLabelEditingBlock({});
    savedSelectionRef.current = null;
    selectedTextRef.current = '';
  }, []);

  // Configure tiptap editor
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        // Disable default features we don't need
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      LinkMentionNode,
      CountdownMentionNode,
      FlowVariableMentionNode,
      TagMark,
      TiptapMention.configure({
        HTMLAttributes: {
          class: 'mention-trigger',
        },
        suggestion: {
          char: '@',
          // Return dummy items to ensure suggestion widget initializes and fires onStart
          // (Empty array prevents widget initialization)
          items: () => [{ id: 'open-link-dialog', label: 'Add link...' }],
          command: ({ editor: currentEditor, range }) => {
            // Delete the @ character
            currentEditor.chain().focus().deleteRange(range).run();
            // For screens without mention menu: open link dialog directly
            if (!showMentionMenu && !linkDialogOpen) {
              handleOpenLinkDialog();
            }
          },
          render: () => ({
            onStart: (props) => {
              // Store the range for later use
              suggestionRangeRef.current = props.range;

              if (showMentionMenu) {
                // Screens with variables: show Link/Variable menu
                const clientRect = props.clientRect?.();
                if (clientRect) {
                  setSuggestionMenuAnchor({
                    getBoundingClientRect: () => clientRect,
                  });
                  setSuggestionMenuOpen(true);
                }
              } else {
                // Screens without variables: open dialog immediately (existing behavior)
                if (!linkDialogOpen && !dialogJustClosedRef.current) {
                  handleOpenLinkDialog();
                }
              }
            },
            onUpdate: (props) => {
              // Update menu position if needed
              if (showMentionMenu && props.clientRect) {
                const clientRect = props.clientRect();
                if (clientRect) {
                  setSuggestionMenuAnchor({
                    getBoundingClientRect: () => clientRect,
                  });
                }
              }
            },
            onKeyDown: ({ event }) => {
              // Forward keyboard events to suggestion menu
              if (showMentionMenu && suggestionMenuRef.current) {
                const handled = suggestionMenuRef.current.onKeyDown(event);
                if (handled) return true;
              }
              if (event.key === 'Escape') {
                setSuggestionMenuOpen(false);
                handleCloseLinkDialog();
                return true;
              }
              return false;
            },
            onExit: () => {
              // Don't close menu here - let click handlers manage it
            },
          }),
        },
      }),
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: true,
        showOnlyCurrent: false,
      }),
    ],
    content: parseTemplateToTiptap(value, existingLinks, existingCountdowns || {}, flowVariableRefs),
    onUpdate: ({ editor: currentEditor }) => {
      // DEBUG: Track editor updates
      console.log('[TiptapTemplateEditor.onUpdate] CALLED', {
        suppressed: suppressUpdateRef.current,
        disabled,
      });

      // Skip if we're in the middle of programmatic insertion (prevents race condition)
      if (suppressUpdateRef.current) return;

      if (!disabled) {
        const templateText = serializeTiptapToTemplate(currentEditor);
        console.log('[TiptapTemplateEditor.onUpdate] calling onChange with:', templateText.slice(0, 50));
        onChange(templateText);
      }

      // Reset dialog flag if text doesn't end with @
      const currentText = currentEditor.getText();
      if (dialogJustClosedRef.current && !currentText.endsWith('@')) {
        dialogJustClosedRef.current = false;
      }
    },
    editorProps: {
      attributes: {
        class: 'tiptap-template-editor',
        'data-testid': testId || 'tiptap-template-editor',
      },
      // Handle backspace/delete for mention nodes
      handleKeyDown: (view, event) => {
        const mentionNodeNames = ['linkMention', 'countdownMention', 'flowVariableMention'];
        if (event.key === 'Backspace') {
          const { selection } = view.state;
          if (selection.empty) {
            const $cursorPos = selection.$from;
            const nodeBefore = $cursorPos.nodeBefore;
            if (nodeBefore && mentionNodeNames.includes(nodeBefore.type.name)) {
              const from = $cursorPos.pos - nodeBefore.nodeSize;
              const to = $cursorPos.pos;
              view.dispatch(view.state.tr.delete(from, to));
              return true;
            }
          }
        }
        if (event.key === 'Delete') {
          const { selection } = view.state;
          if (selection.empty) {
            const $cursorPos = selection.$from;
            const nodeAfter = $cursorPos.nodeAfter;
            if (nodeAfter && mentionNodeNames.includes(nodeAfter.type.name)) {
              const from = $cursorPos.pos;
              const to = $cursorPos.pos + nodeAfter.nodeSize;
              view.dispatch(view.state.tr.delete(from, to));
              return true;
            }
          }
        }
        return false;
      },
    },
  });

  // Update editor editable state when disabled changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  // Sync external value changes
  // CRITICAL FIX: Removed existingLinks from dependencies
  // existingLinks is only used for parsing, not for deciding WHEN to sync
  // Having it in deps caused unnecessary syncs when parent re-rendered with new {} reference
  useEffect(() => {
    if (editor) {
      const currentText = serializeTiptapToTemplate(editor);
      const needsSync = value !== currentText;
      // DEBUG: Track sync effect
      console.log('[TiptapTemplateEditor.useEffect:sync] CALLED', {
        needsSync,
        valueLength: value?.length,
        currentTextLength: currentText?.length,
        valueSample: value?.slice(0, 30),
        currentTextSample: currentText?.slice(0, 30),
      });
      if (needsSync) {
        console.log('[TiptapTemplateEditor.useEffect:sync] Setting content (emitUpdate=false)');
        const jsonContent = parseTemplateToTiptap(value, existingLinks, existingCountdowns || {}, flowVariableRefs);
        editor.commands.setContent(jsonContent, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]); // Removed existingLinks - only value change should trigger sync

  // Handle suggestion menu close (for paywall screens)
  const handleSuggestionMenuClose = useCallback(() => {
    setSuggestionMenuOpen(false);
    setSuggestionMenuAnchor(null);
    suggestionRangeRef.current = null;
    dialogJustClosedRef.current = true;
  }, []);

  // Handle "Link" selection from suggestion menu
  const handleSuggestionSelectLink = useCallback(() => {
    // Delete the @ trigger character before opening dialog
    if (editor && suggestionRangeRef.current) {
      editor.chain().focus().deleteRange(suggestionRangeRef.current).run();
      suggestionRangeRef.current = null;
    }
    setSuggestionMenuOpen(false);
    handleOpenLinkDialog();
  }, [editor, handleOpenLinkDialog]);

  // Handle variable selection from suggestion menu (paywall screens)
  const handleSelectVariable = useCallback(
    (variable: PaywallVariable) => {
      // Store range locally immediately (before any state changes that might clear it)
      const range = suggestionRangeRef.current;
      if (!editor || !range) {
        console.warn('[TiptapTemplateEditor] Cannot insert variable: editor or range not available');
        return;
      }

      // Suppress onUpdate during insertion
      suppressUpdateRef.current = true;

      try {
        // Delete the @ character that triggered the menu (using local range copy)
        editor.chain().focus().deleteRange(range).run();

        // Insert the variable as plain text (e.g., "@price")
        editor.chain().focus().insertContent(`@${variable.name} `).run();

        // Serialize and update
        const templateText = serializeTiptapToTemplate(editor);
        onChange(templateText);
      } finally {
        suppressUpdateRef.current = false;
      }

      // Close menu
      setSuggestionMenuOpen(false);
      setSuggestionMenuAnchor(null);
      suggestionRangeRef.current = null;
      dialogJustClosedRef.current = true;
    },
    [editor, onChange]
  );

  // Handle flow variable selection from suggestion menu (V2 screens)
  const handleSelectFlowVariable = useCallback(
    (item: FlowVariableItem) => {
      const range = suggestionRangeRef.current;
      if (!editor || !range) {
        console.warn('[TiptapTemplateEditor] Cannot insert flow variable: editor or range not available');
        return;
      }

      suppressUpdateRef.current = true;

      try {
        // Delete the @ character that triggered the menu
        editor.chain().focus().deleteRange(range).run();

        // Insert as styled flowVariableMention node (green chip)
        editor.chain().focus().insertContent([
          { type: 'flowVariableMention', attrs: { ref: item.ref } },
          { type: 'text', text: ' ' },
        ]).run();

        // Serialize and update
        const templateText = serializeTiptapToTemplate(editor);
        onChange(templateText);
      } finally {
        suppressUpdateRef.current = false;
      }

      // Close menu
      setSuggestionMenuOpen(false);
      setSuggestionMenuAnchor(null);
      suggestionRangeRef.current = null;
      dialogJustClosedRef.current = true;
    },
    [editor, onChange]
  );

  // Handle "Countdown" selection from suggestion menu — opens countdown dialog
  const handleSuggestionSelectCountdown = useCallback(() => {
    // Delete the @ trigger character before opening dialog
    if (editor && suggestionRangeRef.current) {
      editor.chain().focus().deleteRange(suggestionRangeRef.current).run();
      suggestionRangeRef.current = null;
    }
    setSuggestionMenuOpen(false);
    setCountdownDialogOpen(true);
  }, [editor]);

  // Handle countdown dialog submit — inserts countdownMention node
  const handleAddCountdownSubmit = useCallback(
    (name: string, config: InlineCountdownConfig) => {
      if (!editor) return;

      suppressUpdateRef.current = true;

      try {
        // Insert countdown mention node
        const insertPos = editor.state.selection.to;
        const mentionNode = {
          type: 'countdownMention',
          attrs: { id: name },
        };

        editor
          .chain()
          .focus()
          .insertContentAt(insertPos, [mentionNode, { type: 'text', text: ' ' }])
          .run();

        // Serialize and pass to onAddCountdown
        const templateText = serializeTiptapToTemplate(editor);
        onAddCountdown?.(name, config, templateText);
      } finally {
        suppressUpdateRef.current = false;
      }

      setCountdownDialogOpen(false);
    },
    [editor, onAddCountdown]
  );

  // Get information about current selection (link, countdown, label, or plain text)
  const getSelectionInfo = useCallback((): SelectionInfo => {
    if (!editor) return null;
    const { selection } = editor.state;

    // Check for atomic node selection (LinkMention or CountdownMention)
    // NodeSelection has 'node' property, use type assertion
    const nodeSelection = selection as { node?: { type: { name: string }; attrs: Record<string, string> } };
    if (nodeSelection.node?.type.name === 'linkMention') {
      return {
        type: 'link',
        id: nodeSelection.node.attrs.id,
        url: nodeSelection.node.attrs.url,
        labelTag: nodeSelection.node.attrs.labelTag || null,
        from: selection.from,
        to: selection.to,
      };
    }
    if (nodeSelection.node?.type.name === 'countdownMention') {
      return {
        type: 'countdown',
        id: nodeSelection.node.attrs.id,
        labelTag: nodeSelection.node.attrs.labelTag || null,
        from: selection.from,
        to: selection.to,
      };
    }
    if (nodeSelection.node?.type.name === 'flowVariableMention') {
      return {
        type: 'flowVariable',
        id: nodeSelection.node.attrs.ref,
        labelTag: nodeSelection.node.attrs.labelTag || null,
        from: selection.from,
        to: selection.to,
      };
    }

    // Check for TagMark (text with template tag mark)
    if (!selection.empty) {
      const { from, to } = selection;
      const markAttrs = editor.getAttributes('templateTag');
      if (markAttrs.tagName) {
        return {
          type: 'label',
          tagName: markAttrs.tagName,
          from,
          to,
        };
      }
    }

    // Plain text selection
    if (!selection.empty) {
      return {
        type: 'text',
        from: selection.from,
        to: selection.to,
      };
    }

    return null;
  }, [editor]);

  // Handle Delete Countdown — removes countdown node from text
  const handleDeleteCountdown = useCallback(() => {
    if (!editor) return;
    const info = getSelectionInfo();
    if (info?.type !== 'countdown') return;

    suppressUpdateRef.current = true;

    try {
      editor.chain().focus().deleteRange({ from: info.from, to: info.to }).run();
      const templateText = serializeTiptapToTemplate(editor);
      onDeleteCountdown?.(info.id, templateText);
    } finally {
      suppressUpdateRef.current = false;
    }
  }, [editor, getSelectionInfo, onDeleteCountdown]);

  // Handle Edit Link - opens dialog with existing data
  const handleEditLink = useCallback(() => {
    const info = getSelectionInfo();
    if (info?.type !== 'link') return;

    savedSelectionRef.current = { from: info.from, to: info.to };
    setLinkEditingKey(info.id);
    setLinkEditingUrl(info.url);
    setLinkDialogMode('edit');
    setLinkDialogOpen(true);
  }, [getSelectionInfo]);

  // Handle Edit Label - opens dialog with existing styles
  const handleEditLabel = useCallback(() => {
    const info = getSelectionInfo();
    if (info?.type !== 'label') return;

    savedSelectionRef.current = { from: info.from, to: info.to };
    setLabelEditingKey(info.tagName);
    setLabelEditingBlock(existingLabels[info.tagName] || {});
    setLabelDialogMode('edit');
    setLabelDialogOpen(true);
  }, [getSelectionInfo, existingLabels]);

  // Handle Delete Link - removes link node from text
  const handleDeleteLink = useCallback(() => {
    if (!editor) return;
    const info = getSelectionInfo();
    if (info?.type !== 'link') return;

    // Suppress onUpdate during deletion
    suppressUpdateRef.current = true;

    try {
      // Delete the link node
      editor.chain().focus().deleteRange({ from: info.from, to: info.to }).run();

      // Serialize and notify parent
      const templateText = serializeTiptapToTemplate(editor);
      onDeleteLink?.(info.id, templateText);
    } finally {
      suppressUpdateRef.current = false;
    }
  }, [editor, getSelectionInfo, onDeleteLink]);

  // Handle Delete Label - removes tag mark from text (text remains)
  const handleDeleteLabel = useCallback(() => {
    if (!editor) return;
    const info = getSelectionInfo();
    if (info?.type !== 'label') return;

    // Suppress onUpdate during deletion
    suppressUpdateRef.current = true;

    try {
      // Remove the tag mark (text stays, just unwrapped)
      editor
        .chain()
        .focus()
        .setTextSelection({ from: info.from, to: info.to })
        .unsetMark('templateTag')
        .run();

      // Serialize and notify parent
      const templateText = serializeTiptapToTemplate(editor);
      onDeleteLabel?.(info.tagName, templateText);
    } finally {
      suppressUpdateRef.current = false;
    }
  }, [editor, getSelectionInfo, onDeleteLabel]);

  // Handle Highlight Mention - wrap an atom mention in a label
  const handleHighlightMention = useCallback(() => {
    const info = getSelectionInfo();
    if (info?.type !== 'link' && info?.type !== 'countdown' && info?.type !== 'flowVariable') return;

    // Save selection for setting labelTag after label creation
    savedSelectionRef.current = { from: info.from, to: info.to };
    selectedTextRef.current = info.id; // Pre-fill with mention name
    setLabelDialogMode('add');
    setLabelDialogOpen(true);
  }, [getSelectionInfo]);

  // Handle Remove Highlight from Mention - clear labelTag attribute
  const handleRemoveHighlightMention = useCallback(() => {
    if (!editor) return;
    const info = getSelectionInfo();
    if (info?.type !== 'link' && info?.type !== 'countdown' && info?.type !== 'flowVariable') return;

    suppressUpdateRef.current = true;
    try {
      const nodeAtPos = editor.state.doc.nodeAt(info.from);
      if (nodeAtPos) {
        const tr = editor.state.tr.setNodeMarkup(info.from, undefined, {
          ...nodeAtPos.attrs,
          labelTag: null,
        });
        editor.view.dispatch(tr);
      }
      const templateText = serializeTiptapToTemplate(editor);
      onChange(templateText);
    } finally {
      suppressUpdateRef.current = false;
    }
  }, [editor, getSelectionInfo, onChange]);

  // Handle link dialog submit
  const handleAddLinkSubmit = useCallback(
    (name: string, linkData: LinkData) => {
      if (!editor) return;

      // Suppress onUpdate while we modify editor (prevents intermediate updates)
      suppressUpdateRef.current = true;

      try {
        // Insert link mention node
        const insertPos = editor.state.selection.to;
        const mentionNode = {
          type: 'linkMention',
          attrs: { id: name, url: linkData.url },
        };

        editor
          .chain()
          .focus()
          .insertContentAt(insertPos, [mentionNode, { type: 'text', text: ' ' }])
          .run();

        // CRITICAL: Serialize the text (with @linkName) and pass to onAddLink
        // This ensures both the link AND text are updated in a single atomic operation
        const templateText = serializeTiptapToTemplate(editor);
        onAddLink(name, linkData.url, templateText);
      } finally {
        // Re-enable onUpdate
        suppressUpdateRef.current = false;
      }

      handleCloseLinkDialog();
    },
    [editor, onAddLink, handleCloseLinkDialog]
  );

  // Handle "Create Tag" button click
  const handleCreateTagClick = useCallback(() => {
    if (!editor) return;

    // Save current selection
    const { from, to } = editor.state.selection;
    if (from === to) return; // No selection

    // Capture selected text for pre-filling label name
    const selectedText = editor.state.doc.textBetween(from, to);
    selectedTextRef.current = selectedText;

    savedSelectionRef.current = { from, to };
    handleOpenLabelDialog();
  }, [editor, handleOpenLabelDialog]);

  // Handle label dialog submit
  const handleAddLabelSubmit = useCallback(
    (name: string, labelBlock: Partial<LabelBlockType>) => {
      if (!editor || !savedSelectionRef.current) return;

      // Suppress onUpdate while we modify editor (prevents intermediate updates)
      suppressUpdateRef.current = true;

      try {
        const { from } = savedSelectionRef.current;
        const nodeAtPos = editor.state.doc.nodeAt(from);

        // Check if selection is on a mention node
        if (nodeAtPos && ['linkMention', 'countdownMention', 'flowVariableMention'].includes(nodeAtPos.type.name)) {
          // Set labelTag attribute on the mention node instead of applying mark
          const tr = editor.state.tr.setNodeMarkup(from, undefined, {
            ...nodeAtPos.attrs,
            labelTag: name,
          });
          editor.view.dispatch(tr);
        } else {
          // Regular text selection: apply tag mark
          const { to } = savedSelectionRef.current;
          editor
            .chain()
            .focus()
            .setTextSelection({ from, to })
            .setTagMark(name)
            .run();
        }

        // CRITICAL: Serialize the text and pass to onAddLabel
        // This ensures both the label AND text are updated in a single atomic operation
        const templateText = serializeTiptapToTemplate(editor);
        onAddLabel(name, labelBlock, templateText);
      } finally {
        // Re-enable onUpdate
        suppressUpdateRef.current = false;
      }

      handleCloseLabelDialog();
    },
    [editor, onAddLabel, handleCloseLabelDialog]
  );

  // Focus editor on container click
  // CRITICAL FIX: Suppress onUpdate during programmatic focus to prevent race conditions
  // When user clicks on editor after changing a style, focus() can trigger onUpdate
  // which may overwrite pending style changes with stale data
  const handleContainerClick = useCallback(() => {
    if (!editor) return;

    // Suppress onUpdate during programmatic focus
    suppressUpdateRef.current = true;
    editor.chain().focus().run();

    // Re-enable after microtask (focus is synchronous)
    queueMicrotask(() => {
      suppressUpdateRef.current = false;
    });
  }, [editor]);

  if (!editor) {
    return <Box sx={{ minHeight: MAIN_FIELD_HEIGHT }}>Loading editor...</Box>;
  }

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={editorContainerSx}
        onClick={handleContainerClick}
      >
        <EditorContent editor={editor} />

        {/* BubbleMenu - contextual based on selection type */}
        <BubbleMenu
          editor={editor}
          shouldShow={({ editor: ed, state }) => {
            // Show only when something is selected (not empty) and editor is editable
            return !state.selection.empty && ed.isEditable;
          }}
          tippyOptions={{
            placement: 'top',
            duration: 100,
          }}
        >
          <Paper sx={bubbleMenuPaperSx}>
            {(() => {
              const info = getSelectionInfo();

              // Countdown selected: Delete + Highlight/Remove Highlight
              if (info?.type === 'countdown') {
                return (
                  <>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon fontSize="small" />}
                      onClick={handleDeleteCountdown}
                      color="error"
                      sx={{ textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    {info.labelTag ? (
                      <Button
                        size="small"
                        startIcon={<HighlightIcon fontSize="small" />}
                        onClick={handleRemoveHighlightMention}
                        sx={{ textTransform: 'none' }}
                      >
                        Remove Highlight
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<HighlightIcon fontSize="small" />}
                        onClick={handleHighlightMention}
                        sx={{ textTransform: 'none' }}
                      >
                        Highlight
                      </Button>
                    )}
                  </>
                );
              }

              // Flow variable selected: Delete + Highlight/Remove Highlight (same as countdown)
              if (info?.type === 'flowVariable') {
                return (
                  <>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon fontSize="small" />}
                      onClick={() => {
                        if (!editor || !info) return;
                        editor.chain().focus().deleteRange({ from: info.from, to: info.to }).run();
                        const templateText = serializeTiptapToTemplate(editor);
                        onChange(templateText);
                      }}
                      color="error"
                      sx={{ textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    {info.labelTag ? (
                      <Button
                        size="small"
                        startIcon={<HighlightIcon fontSize="small" />}
                        onClick={handleRemoveHighlightMention}
                        sx={{ textTransform: 'none' }}
                      >
                        Remove Highlight
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<HighlightIcon fontSize="small" />}
                        onClick={handleHighlightMention}
                        sx={{ textTransform: 'none' }}
                      >
                        Highlight
                      </Button>
                    )}
                  </>
                );
              }

              // Link selected: Edit, Delete, Highlight/Remove Highlight
              if (info?.type === 'link') {
                return (
                  <>
                    <Button
                      size="small"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={handleEditLink}
                      sx={{ textTransform: 'none' }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon fontSize="small" />}
                      onClick={handleDeleteLink}
                      color="error"
                      sx={{ textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    {info.labelTag ? (
                      <Button
                        size="small"
                        startIcon={<HighlightIcon fontSize="small" />}
                        onClick={handleRemoveHighlightMention}
                        sx={{ textTransform: 'none' }}
                      >
                        Remove Highlight
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<HighlightIcon fontSize="small" />}
                        onClick={handleHighlightMention}
                        sx={{ textTransform: 'none' }}
                      >
                        Highlight
                      </Button>
                    )}
                  </>
                );
              }

              // Label selected: Edit, Delete
              if (info?.type === 'label') {
                return (
                  <>
                    <Button
                      size="small"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={handleEditLabel}
                      sx={{ textTransform: 'none' }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon fontSize="small" />}
                      onClick={handleDeleteLabel}
                      color="error"
                      sx={{ textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                  </>
                );
              }

              // Plain text: Highlight
              return (
                <Button
                  size="small"
                  startIcon={<HighlightIcon fontSize="small" />}
                  onClick={handleCreateTagClick}
                  sx={{ textTransform: 'none' }}
                >
                  Highlight
                </Button>
              );
            })()}
          </Paper>
        </BubbleMenu>
      </Paper>

      {/* Link Dialog */}
      <AddLinkDialog
        open={linkDialogOpen}
        onClose={handleCloseLinkDialog}
        onAdd={handleAddLinkSubmit}
        existingKeys={Object.keys(existingLinks)}
        predecessorScreens={predecessorScreens}
        mode={linkDialogMode}
        editingKey={linkEditingKey}
        initialUrl={linkEditingUrl}
      />

      {/* Label Dialog */}
      <AddLabelDialog
        open={labelDialogOpen}
        onClose={handleCloseLabelDialog}
        onAdd={handleAddLabelSubmit}
        existingKeys={Object.keys(existingLabels)}
        initialName={labelDialogMode === 'edit' ? labelEditingKey : selectedTextRef.current}
        initialLabelBlock={labelDialogMode === 'edit' ? labelEditingBlock : parentTextStyles}
        mode={labelDialogMode}
        editingKey={labelEditingKey}
      />

      {/* Mention Suggestion Menu (Link / Countdown / Variable choice) */}
      {showMentionMenu && (
        <MentionSuggestionMenu
          ref={suggestionMenuRef}
          anchorEl={suggestionMenuAnchor}
          open={suggestionMenuOpen}
          onClose={handleSuggestionMenuClose}
          onSelectLink={handleSuggestionSelectLink}
          onSelectCountdown={hasCountdowns ? handleSuggestionSelectCountdown : undefined}
          onSelectVariable={showPaywallVariables ? handleSelectVariable : undefined}
          flowVariables={flowVariables}
          onSelectFlowVariable={handleSelectFlowVariable}
        />
      )}

      {/* Countdown Dialog */}
      {hasCountdowns && (
        <AddCountdownDialog
          open={countdownDialogOpen}
          onClose={() => setCountdownDialogOpen(false)}
          onAdd={handleAddCountdownSubmit}
          existingKeys={Object.keys(existingCountdowns)}
        />
      )}
    </Box>
  );
}

export default TiptapTemplateEditor;
